import {
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';

import { assembleLocationString } from '../../utils/DrupalEntityTypes/Residence/ResidenceHelpers';
import { createBedBathSqFtTableData } from '../../utils/DrupalEntityTypes/FloorPlan/FloorPlanHelpers';
import { useAppStore, useConfigStore } from '../../stores';
import { createSkeletonItem } from '../Skeleton/SkeletonHelpers';
import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import useHeartbeat from '../../hooks/useHeartbeat';
import ResidenceStatusWidget from './ResidenceStatusWidget/ResidenceStatusWidget';

const auth = getAuthClient();

/**
 * useResidenceDetail - Custom hook for residence detail data fetching and state management.
 * Shared logic between MapResidenceDetail (modal) and PibResidenceDetail (PIB page).
 *
 * Polls the heartbeat endpoint every ~1 second while a residence detail is open.
 * When the residence version changes (e.g., status or residents updated in CMS),
 * fresh residence data is fetched automatically.
 *
 * @param {string} uuid - The residence UUID
 * @param {object} data - Initial/partial data for the residence
 * @returns {object} - All state and data needed for residence detail rendering
 */
const useResidenceDetail = ( uuid, data ) => {
  const [ unitData, setUnitData ] = useState( null );
  const [ fetchUnitData, setFetchUnitData ] = useState( true );
  const [ detailDialogData, setDetailDialogData ] = useState( { status: null, message: null } );
  const [ tableInfo, setTableInfo ] = useState( null );

  const initializedRef = useRef( false );
  const lastUuidRef = useRef( null );
  const dataVersionRef = useRef( null );

  // Zustand stores
  const pricingTriggered = useAppStore( ( state ) => state.pricingTriggered );
  const setPricingTriggered = useAppStore( ( state ) => state.setPricingTriggered );
  const settings = useAppStore( ( state ) => state.settings );
  const featureFlags = useConfigStore( ( state ) => state.featureFlags );

  const { platformShowPricing, platformShowPricingInitially, platformShowOccupancy } = featureFlags || {};
  const { platform, urlSlug, mode } = settings || {};

  // Destructure commonly used fields from unitData
  const {
    title,
    description,
    feeData,
    buildingGroupingName,
    levelName,
    residents,
    statusUuid,
    statusName,
    svgId,
    floorPlanUuid,
  } = unitData || {};
  const { hasFees } = feeData || {};

  // Heartbeat callback: when residence version changes, refetch residence data.
  const handleResidenceVersionChange = useCallback( () => {
    setFetchUnitData( true );
  }, [] );

  // Poll heartbeat while a residence detail is open.
  // The heartbeat hook's first response establishes a baseline version
  // (no callback fired), so it's safe to start immediately.
  useHeartbeat( urlSlug, platform, {
    onResidenceVersionChange: handleResidenceVersionChange,
    enabled: !!uuid,
    mode,
  } );

  // Get unit data on load or if we get an updated dialog message (which was
  // triggered by change to the residence)
  useEffect( () => {
    // Reset state if uuid changed
    if ( lastUuidRef.current !== uuid ) {
      initializedRef.current = false;
      lastUuidRef.current = uuid;
      dataVersionRef.current = null;
      // Clear previous residence data so we don't use stale svgId, etc.
      setUnitData( null );
      setFetchUnitData( true );
    }

    // Only initialize once, or when fetchUnitData flag is set (for refresh after status change)
    if ( initializedRef.current && !fetchUnitData ) {
      return;
    }

    if ( uuid ) {
      // If we have partial data already, set it immediately for faster rendering
      if ( data && !initializedRef.current ) {
        setUnitData( data );
      }

      const fetchData = async () => {
        const params = mode ? { mode } : {};
        const url = buildYourTourApiUrl( `/your_tour_api/residence/${uuid}`, params );
        const response = await auth.fetchWithAuthentication(
          url,
          'GET',
        );

        if ( response && !response.error ) {
          // @TODO - the dataVersion skip optimization from 97e4acdf was suppressing
          // setUnitData for newly-selected PIB residence subpanes (no merged data ever
          // applied). Removed for correctness; revisit once the heartbeat/initial-fetch
          // ordering is understood.
          dataVersionRef.current = response.dataVersion || null;

          // Map some fields for compatibility with existing components.
          const rawData = {
            ...response,
            // Ensure roomData is mapped to roomDims for nav button checks
            roomDims: response.roomData,
            // Map floor plan images for nav button checks
            twoDFloorPlan: response.twoD,
            threeDFloorPlan: response.threeD,
            // Map virtual tour for nav button checks
            virtualTour: response.threeSixties?.length > 0 ? { threeSixties: response.threeSixties } : null,
            // Map photo gallery for nav button checks
            photoGallery: response.photoGalleryImages?.length > 0 ? { images: response.photoGalleryImages } : null,
          };
          // Merge with any existing data to preserve fields we already had
          setUnitData( ( prevData ) => ( { ...prevData, ...rawData } ) );
        }
        setFetchUnitData( false );
        initializedRef.current = true;
      };
      fetchData();
    } else if ( data ) {
      // When data is already available and no uuid to fetch, use it immediately
      setUnitData( data );
      setFetchUnitData( false );
      initializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ uuid, fetchUnitData, mode ] );

  // Create table info
  useEffect(() => {
    if (!unitData) {
      // Show 2 skeleton list items while loading
      const skeletonListData = [];
      for (let i = 0; i < 2; i++) {
        skeletonListData.push({
          key: `skeleton-residence-list-${i}`,
          note: createSkeletonItem('div', 'detailTable__itemNote', 'full', true),
        });
      }
      setTableInfo(skeletonListData);
    } else {
      let rawTableInfo = [];

      // PIB needs to show beds/bath, YT does not.
      if (platform === 'page' && unitData) {
        rawTableInfo = createBedBathSqFtTableData(unitData);
      }

      // Add location to tableInfo.
      if (buildingGroupingName && levelName) {
        const location = assembleLocationString(buildingGroupingName, levelName);
        rawTableInfo.push({
          label: 'Location',
          data: location,
        });
      }

      // Show residence status/occupancy in modal on YT, Kiosk, SiteSee, and PIB only when occupancy is enabled.
      // Widget honors each platform's feature flags (e.g. kiosk_show_occupancy, occupants on/off).
      if (
        ( platform === 'yt' || platform === 'kiosk' || platform === 'siteSee' || platform === 'page' )
        && platformShowOccupancy
        && statusUuid
      ) {
        const residenceStatus = (
          <ResidenceStatusWidget
            residenceUuid={uuid}
            residenceTitle={title}
            residenceSvgId={svgId}
            residents={residents}
            statusUuid={statusUuid}
            residenceStatus={statusName}
            setDetailDialogData={setDetailDialogData}
            setFetchUnitData={setFetchUnitData}
          />
        );

        rawTableInfo.push({
          component: residenceStatus,
        });
      }

      setTableInfo(rawTableInfo);
    }
  }, [
    unitData,
    platform,
    platformShowOccupancy,
    uuid,
    title,
    residents,
    statusUuid,
    statusName,
    buildingGroupingName,
    levelName,
  ]);

  const isLoading = fetchUnitData;
  const isDataLoaded = unitData !== null && !fetchUnitData;

  return {
    // Core data
    unitData,
    isLoading,
    isDataLoaded,

    // Derived fields from unitData
    title,
    description,
    feeData,
    hasFees,
    svgId,
    floorPlanUuid,

    // Table and dialog
    tableInfo,
    detailDialogData,

    // Pricing state (from stores)
    pricingTriggered,
    setPricingTriggered,
    platformShowPricing,
    platformShowPricingInitially,
  };
};

export default useResidenceDetail;
